import { err, ok, type AppResult } from "@gladis/shared";

import { CardIdentifierInvalidError } from "../../errors/index.js";

/**
 * Enough of an account identifier to tell two cards from the same bank apart — and nothing more.
 *
 * WHAT IS DELIBERATELY ABSENT, AND WHY IT IS A REQUIREMENT RATHER THAN AN OMISSION.
 *
 * There is no full card number and no full account number, anywhere: no field, no column, no route, no
 * display. Two independent reasons, and either alone would be sufficient:
 *
 *   1. Nothing in this application ever initiates a payment, so a full number has no use here. A value
 *      held for no purpose is a liability with no offsetting benefit.
 *   2. The local database is dumped to a plaintext file as part of the normal development workflow
 *      (`pnpm db:dump`), so anything held is held in the clear on a laptop.
 *
 * Both halves are optional. A card with no identifier at all is still a card — a standing transfer or a
 * direct debit mandate may have nothing worth showing — and the display omits the line entirely rather
 * than rendering an empty run of bullets.
 */

/** Two to eleven characters: long enough for a BIC, short enough that no account number fits. */
const BANK_CODE_PATTERN = /^[A-Z0-9]{2,11}$/;
const LAST_FOUR_PATTERN = /^\d{4}$/;

/** The masking character used in the display form. `INGB •••• 4488`. */
const MASK = "••••";

export class BankCode {
  private constructor(private readonly value: string) {}

  /**
   * Upper-cased on construction rather than demanded of the caller.
   *
   * A member typing `ingb` is helped rather than corrected, and the database's own
   * `chk_card_bank_code` check only admits upper case — so normalising here is what keeps that check a
   * backstop instead of the thing the member meets.
   */
  static create(input: string): AppResult<BankCode> {
    const normalised = input.trim().toUpperCase();
    if (!BANK_CODE_PATTERN.test(normalised)) {
      return err(
        new CardIdentifierInvalidError(
          "A bank code is two to eleven letters or digits, like INGB.",
          { bankCode: input },
        ),
      );
    }
    return ok(new BankCode(normalised));
  }

  static trusted(value: string): BankCode {
    return new BankCode(value);
  }

  toString(): string {
    return this.value;
  }
}

export class LastFour {
  private constructor(private readonly value: string) {}

  /**
   * Exactly four digits, held as a STRING.
   *
   * `0042` is the case that decides this. As a number it arrives as `42`, and no validation downstream
   * can recover the two digits that were dropped before anything ran — so the wire schema, this value
   * object and the column are all text, and the leading zeros survive all three.
   */
  static create(input: string): AppResult<LastFour> {
    const trimmed = input.trim();
    if (!LAST_FOUR_PATTERN.test(trimmed)) {
      return err(
        new CardIdentifierInvalidError("Enter exactly the last four digits.", { lastFour: input }),
      );
    }
    return ok(new LastFour(trimmed));
  }

  static trusted(value: string): LastFour {
    return new LastFour(value);
  }

  toString(): string {
    return this.value;
  }
}

/**
 * The display form, and FR-051's four cases decided in ONE place.
 *
 *   both        → `INGB •••• 4488`
 *   code only   → `INGB`
 *   digits only → `•••• 4488`
 *   neither     → `null`, and the interface renders no identifier line at all
 *
 * `null` rather than an empty string, so a card with no identifier cannot render as a stray run of
 * bullets or an unexplained gap. Assembled server-side because four cases decided in two places become
 * five cases.
 */
export function identifierLabel(
  bankCode: BankCode | null,
  lastFour: LastFour | null,
): string | null {
  if (bankCode && lastFour) return `${bankCode.toString()} ${MASK} ${lastFour.toString()}`;
  if (bankCode) return bankCode.toString();
  if (lastFour) return `${MASK} ${lastFour.toString()}`;
  return null;
}

/**
 * What a screen reader is given instead of the masked form (FR-056).
 *
 * Four `•` characters are announced as anything from silence to "bullet bullet bullet bullet", and
 * neither is the fact. "INGB, ending 4488" is.
 */
export function identifierAccessibleLabel(
  bankCode: BankCode | null,
  lastFour: LastFour | null,
): string | null {
  if (bankCode && lastFour) return `${bankCode.toString()}, ending ${lastFour.toString()}`;
  if (bankCode) return bankCode.toString();
  if (lastFour) return `ending ${lastFour.toString()}`;
  return null;
}
